// color-exempt-file — retokenisation Phase 6 ui-themes : dark:bg-[#030712] preserve le rendu dark historique (voir memoire ui-themes) ; Zap icon jaune (accent decoratif fixe, non lie au theme)
import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router-dom";
import { SEOHead } from "../components/SEOHead";
import { CalendarDays, ArrowRight, BookOpen, Zap } from "lucide-react";
import { ProductHeader } from "@core/components/layout/products/ProductHeader";
import { ProductSidebar } from "@core/components/layout/products/ProductSidebar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@core/components/ui/accordion";
import { getGuides, getGuideList } from "./guidesData";
import { PublicFooter } from "../components/PublicFooter";
import { localizedPath } from "../seo/enRoutes";

export default function GuidePage() {
  const { t, i18n } = useTranslation(["polls", "common"]);
  const { slug } = useParams<{ slug: string }>();
  const { pathname } = useLocation();
  // Sur /en/guides/…, les liens internes restent sur la version anglaise.
  const to = (target: string) => localizedPath(pathname, target);
  const guides = getGuides(i18n.language);
  const guideList = getGuideList(i18n.language);
  const guide = slug ? guides[slug] : undefined;

  if (!guide) {
    return (
      <div className="flex min-h-screen-safe bg-background dark:bg-[#030712] text-foreground">
        <SEOHead title={t("polls:guide_not_found")} noIndex />
        <ProductSidebar productType="date" />
        <div className="flex-1">
          <ProductHeader productType="date" showAuth={true} />
          <div className="flex items-center justify-center py-32">
            <p className="text-muted-foreground">
              {t("polls:guide_not_found")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen-safe bg-background dark:bg-[#030712] text-foreground">
      <SEOHead
        title={guide.title}
        description={guide.metaDescription}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          name: guide.title,
          mainEntity: guide.faq.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: { "@type": "Answer", text: item.answer },
          })),
        }}
      />
      <ProductSidebar productType="date" />
      <div className="flex-1 min-w-0">
        <ProductHeader productType="date" showAuth={true} />

        <div className="flex">
          {/* Sidebar guides */}
          <aside className="hidden lg:block w-56 shrink-0 border-r border-border py-8 px-4 sticky top-0 h-screen overflow-y-auto">
            <Link
              to={to("/guides")}
              className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4 hover:text-foreground transition-colors"
            >
              <BookOpen className="w-3.5 h-3.5" />
              {t("polls:guide_all_guides")}
            </Link>
            <nav className="space-y-0.5">
              {guideList.map((g) => (
                <Link
                  key={g.slug}
                  to={to(`/guides/${g.slug}`)}
                  className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                    g.slug === guide.slug
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent"
                  }`}
                >
                  {g.title}
                </Link>
              ))}
            </nav>
          </aside>

          <article className="flex-1 min-w-0 max-w-3xl mx-auto px-6 py-16">
            {/* Breadcrumb */}
            <nav className="text-xs text-muted-foreground mb-8">
              <Link
                to={to("/")}
                className="hover:text-foreground transition-colors"
              >
                {t("polls:guide_breadcrumb_home")}
              </Link>
              <span className="mx-2">·</span>
              <Link
                to={to("/guides")}
                className="hover:text-foreground transition-colors"
              >
                {t("polls:guide_breadcrumb_guides")}
              </Link>
              <span className="mx-2">·</span>
              <span className="text-muted-foreground">{guide.title}</span>
            </nav>

            {/* H1 */}
            <h1 className="text-3xl sm:text-4xl font-bold mb-6 leading-tight">
              {guide.title}
            </h1>

            {/* Intro */}
            <p className="text-lg text-muted-foreground leading-relaxed mb-12 border-l-2 border-primary/40 pl-4">
              {guide.intro}
            </p>

            {/* Steps */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-8">
                {t("polls:guide_in_steps_prefix")}{" "}
                <span className="text-primary">
                  {t("polls:guide_in_steps_count")}
                </span>
              </h2>
              <div className="space-y-6">
                {guide.steps.map((step, i) => (
                  <div
                    key={i}
                    className="flex gap-4 p-6 rounded-xl border border-border bg-card"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-sm flex-shrink-0 mt-0.5">
                      {i + 1}
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">{step.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {step.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Features DooDates */}
            {guide.features.length > 0 && (
              <section className="mb-12">
                <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-400" />
                  {t("polls:guide_useful_features")}
                </h2>
                <div className="grid sm:grid-cols-3 gap-4">
                  {guide.features.map((f, i) => (
                    <div
                      key={i}
                      className="p-5 rounded-xl border border-primary/20 bg-primary/5"
                    >
                      <h3 className="font-semibold text-sm mb-2 text-primary">
                        {f.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {f.desc}
                      </p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  <Link
                    to={to("/guides/fonctionnalites-avancees")}
                    className="hover:text-foreground transition-colors underline underline-offset-2"
                  >
                    {t("polls:guide_see_all_features")}
                  </Link>
                </p>
              </section>
            )}

            {/* CTA */}
            <div className="my-12 p-8 rounded-2xl bg-primary/10 border border-primary/20 text-center">
              <p className="text-lg font-semibold mb-2">
                {t("polls:guide_cta_ready")}
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                {t("polls:guide_cta_subtitle")}
              </p>
              <Link
                to={guide.ctaHref}
                className="inline-flex items-center gap-2 px-8 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-xl transition-all shadow-lg shadow-primary/20"
              >
                <CalendarDays className="w-4 h-4" />
                {guide.ctaLabel}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* FAQ */}
            <section>
              <h2 className="text-2xl font-bold mb-8">
                {t("polls:guide_faq_title")}
              </h2>
              <Accordion type="single" collapsible className="space-y-2">
                {guide.faq.map((item, i) => (
                  <AccordionItem
                    key={i}
                    value={`q${i}`}
                    className="group border border-border rounded-xl px-6 bg-card"
                  >
                    <AccordionTrigger className="text-left hover:no-underline text-foreground hover:text-primary">
                      {item.question}
                    </AccordionTrigger>
                    {/* forceMount : réponses dans le HTML pré-rendu (SEO) ;
                        masquées en CSS tant que la question est fermée. */}
                    <AccordionContent
                      forceMount
                      className="text-muted-foreground leading-relaxed group-data-[state=closed]:hidden"
                    >
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>

            {/* Auteur */}
            <section className="my-12 p-6 rounded-xl border border-border bg-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold flex-shrink-0">
                  N
                </div>
                <div>
                  <h3 className="font-semibold mb-1">NAJUIA</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-2">
                    {t("polls:guide_author_bio")}
                  </p>
                  <Link
                    to="/about"
                    className="text-primary hover:text-primary/80 transition-colors text-xs font-medium"
                  >
                    {t("polls:landing.faq.learn_more")}
                  </Link>
                </div>
              </div>
            </section>

            {/* Footer nav */}
            <div className="mt-16 pt-8 border-t border-border flex items-center justify-between text-sm">
              <Link
                to={to("/guides")}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                {t("polls:guide_back_to_guides")}
              </Link>
              <Link
                to={guide.ctaHref}
                className="text-primary hover:text-primary/80 transition-colors font-medium"
              >
                {guide.ctaLabel} →
              </Link>
            </div>
          </article>
        </div>
      </div>
      <PublicFooter />
    </div>
  );
}
